import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Chip,
  Stack,
  ListItemButton,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { WidgetCard } from './WidgetCard';
import { WidgetItemsModal } from './WidgetItemsModal';
import { MoreItemsBadge } from './MoreItemsBadge';
import { useTodayTasks } from '../../../hooks/useTasks';
import { useTaskMutations } from '../../../hooks/useTaskMutations';
import { TaskPriorityBadge } from '../../tasks/TaskPriorityBadge';
import type { TaskWithClient } from '../../../types/task';

const MAX_VISIBLE_ITEMS = 3;

export const TodayTasksWidget: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const { data, isLoading, error, refetch } = useTodayTasks();
  const { completeTask, reopenTask } = useTaskMutations();
  const navigate = useNavigate();

  const tasks = data?.data?.tasks || [];
  const visibleTasks = tasks.slice(0, MAX_VISIBLE_ITEMS);
  const remainingCount = Math.max(0, tasks.length - MAX_VISIBLE_ITEMS);

  const handleToggleComplete = async (task: TaskWithClient, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      if (task.status === 'completed') {
        await reopenTask.mutateAsync(task._id);
      } else {
        await completeTask.mutateAsync(task._id);
      }
    } catch (error) {
      console.error('Error toggling task:', error);
    }
  };

  const handleTaskClick = (taskId: string) => {
    setModalOpen(false);
    navigate(`/tasks?id=${taskId}`);
  };

  const TaskListItem = ({ task, inModal = false }: { task: TaskWithClient; inModal?: boolean }) => (
    <ListItem
      disablePadding
      sx={{
        opacity: task.status === 'completed' ? 0.6 : 1,
      }}
    >
      <ListItemButton
        onClick={() => handleTaskClick(task._id)}
        sx={{
          py: inModal ? 1.5 : 0.5,
          px: inModal ? 2 : 0,
          borderRadius: inModal ? 0 : 1,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Checkbox
            size="small"
            checked={task.status === 'completed'}
            onClick={(e) => {
              e.stopPropagation();
              handleToggleComplete(task);
            }}
          />
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography
              variant="body2"
              sx={{
                textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {task.title}
            </Typography>
          }
          secondary={
            <Stack direction="row" spacing={0.5} mt={0.25}>
              <TaskPriorityBadge priority={task.priority} size="small" showIcon={false} />
              {task.dueTime && (
                <Chip label={task.dueTime} size="small" variant="outlined" />
              )}
            </Stack>
          }
        />
      </ListItemButton>
    </ListItem>
  );

  const EmptyState = () => (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="100%"
      py={2}
    >
      <CheckCircleIcon sx={{ fontSize: 48, color: 'success.light', mb: 1 }} />
      <Typography variant="body2" color="text.secondary">
        No tasks due today
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Great job staying on top of things!
      </Typography>
    </Box>
  );

  return (
    <>
      <WidgetCard
        title="Today's Tasks"
        icon={<ScheduleIcon color="primary" />}
        isLoading={isLoading}
        error={error as Error | null}
        onRefresh={() => refetch()}
        viewAllLink="/tasks"
        viewAllLabel="View All Tasks"
        minHeight={180}
      >
        {tasks.length === 0 ? (
          <EmptyState />
        ) : (
          <Box display="flex" flexDirection="column" height="100%">
            <List dense disablePadding sx={{ flex: 1 }}>
              {visibleTasks.map((task) => (
                <TaskListItem key={task._id} task={task} />
              ))}
            </List>
            {remainingCount > 0 && (
              <Box display="flex" justifyContent="center" pt={1}>
                <MoreItemsBadge
                  count={remainingCount}
                  onClick={() => setModalOpen(true)}
                  color="primary"
                  label={`+${remainingCount} more tasks. Click to view all`}
                />
              </Box>
            )}
          </Box>
        )}
      </WidgetCard>

      <WidgetItemsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Today's Tasks"
        icon={<ScheduleIcon color="primary" />}
        totalCount={tasks.length}
      >
        {tasks.map((task) => (
          <TaskListItem key={task._id} task={task} inModal />
        ))}
      </WidgetItemsModal>
    </>
  );
};

export default TodayTasksWidget;
